<template>
  <div class="mobile-pager bg-white border-gray-200 p-2 pt-1 pb-1">
    <!-- 分页信息 -->
    <div v-if="showInfo" class="text-center text-xs text-gray-500 mb-1">
      第 {{ currentPage }} / {{ totalPages }} 页，共 {{ total }} 条记录
    </div>
    
    <!-- 分页控制 -->
    <div class="flex items-center justify-between">
      <!-- 上一页按钮 -->
      <button 
        @click="handlePageChange(currentPage - 1)" 
        :disabled="currentPage === 1"
        class="flex items-center justify-center px-2 py-1 bg-gray-100 text-gray-600 rounded-lg disabled:opacity-50 disabled:bg-gray-50 active:bg-gray-200 transition-colors"
      >
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <!-- 页码显示区域 -->
      <div class="flex items-center space-x-2">
        <!-- 首页 -->
        <button 
          v-if="showFirstLast && currentPage > 3"
          @click="handlePageChange(1)"
          class="w-10 h-10 flex items-center justify-center text-gray-600 bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
        >
          1
        </button>
        
        <!-- 省略号 -->
        <span v-if="showFirstLast && currentPage > 4" class="text-gray-400">...</span>
        
        <!-- 当前页前一页 -->
        <button 
          v-if="currentPage > 1"
          @click="handlePageChange(currentPage - 1)"
          class="w-10 h-10 flex items-center justify-center text-gray-600 bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
        >
          {{ currentPage - 1 }}
        </button>
        
        <!-- 当前页 -->
        <button 
          class="w-10 h-10 flex items-center justify-center text-white bg-blue-500 rounded-lg font-medium"
        >
          {{ currentPage }}
        </button>
        
        <!-- 当前页后一页 -->
        <button 
          v-if="currentPage < totalPages"
          @click="handlePageChange(currentPage + 1)"
          class="w-10 h-10 flex items-center justify-center text-gray-600 bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
        >
          {{ currentPage + 1 }}
        </button>
        
        <!-- 省略号 -->
        <span v-if="showFirstLast && currentPage < totalPages - 3" class="text-gray-400">...</span>
        
        <!-- 末页 -->
        <button 
          v-if="showFirstLast && currentPage < totalPages - 2"
          @click="handlePageChange(totalPages)"
          class="w-10 h-10 flex items-center justify-center text-gray-600 bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
        >
          {{ totalPages }}
        </button>
      </div>
      
      <!-- 下一页按钮 -->
      <button 
        @click="handlePageChange(currentPage + 1)" 
        :disabled="currentPage === totalPages"
        class="flex items-center justify-center px-2 py-1 bg-gray-100 text-gray-600 rounded-lg disabled:opacity-50 disabled:bg-gray-50 active:bg-gray-200 transition-colors"
      >
        <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
    
    <!-- 页码大小选择器 (可选) -->
    <div v-if="showSizeChanger" class="mt-3 text-center">
      <select 
        v-model="innerPageSize" 
        class="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        @change="handlePageSizeChange"
      >
        <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}条/页</option>
      </select>
    </div>
    
    <!-- 快速跳转 (可选) -->
    <div v-if="showQuickJumper && totalPages > 5" class="mt-3 flex items-center justify-center space-x-2">
      <span class="text-sm text-gray-500">跳转到</span>
      <input 
        v-model="jumpPage"
        type="number" 
        :min="1" 
        :max="totalPages"
        class="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        @keyup.enter="handleJump"
      />
      <span class="text-sm text-gray-500">页</span>
      <button 
        @click="handleJump"
        class="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
      >
        确定
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch } from 'vue'

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
    default: 1
  },
  totalPages: {
    type: Number,
    required: true
  },
  pageSize: {
    type: Number,
    default: 10
  },
  pageSizeOptions: {
    type: Array,
    default: () => [10, 20, 50]
  },
  showSizeChanger: {
    type: Boolean,
    default: false
  },
  showQuickJumper: {
    type: Boolean,
    default: false
  },
  total: {
    type: Number,
    default: 0
  },
  showInfo: {
    type: Boolean,
    default: true
  },
  showFirstLast: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:currentPage', 'update:pageSize'])

// 内部状态
const innerPageSize = ref(props.pageSize)
const jumpPage = ref('')

// 监听 pageSize 变化
watch(() => props.pageSize, (newPageSize) => {
  innerPageSize.value = newPageSize
}, { immediate: true })

// 页面跳转处理
const handlePageChange = (page) => {
  if (page >= 1 && page <= props.totalPages && page !== props.currentPage) {
    emit('update:currentPage', page)
  }
}

// 页面大小变化处理
const handlePageSizeChange = () => {
  emit('update:pageSize', innerPageSize.value)
  // 切换页面大小后，可能需要调整当前页
  const maxPage = Math.ceil(props.total / innerPageSize.value)
  if (props.currentPage > maxPage) {
    emit('update:currentPage', Math.max(1, maxPage))
  }
}

// 快速跳转处理
const handleJump = () => {
  const page = parseInt(jumpPage.value)
  if (page && page >= 1 && page <= props.totalPages) {
    handlePageChange(page)
    jumpPage.value = ''
  }
}
</script>

<style scoped>
.mobile-pager {
  /* 确保在移动端有足够的触摸区域 */
  min-height: 60px;
}

/* 移动端优化的按钮样式 */
button {
  min-height: 40px; /* iOS 推荐的最小触摸区域 */
  touch-action: manipulation; /* 优化触摸响应 */
}

/* 输入框在移动端的优化 */
input[type="number"] {
  -webkit-appearance: none;
  -moz-appearance: textfield;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* 活跃状态的视觉反馈 */
@media (hover: none) {
  button:hover {
    /* 在触摸设备上禁用 hover 效果 */
  }
}
</style>